import React, { useState } from 'react';
import styles from './SeatModeDialog.module.css';
import { setSeatMode } from './api/vlListing.api';
import { planSeatMode, seatModeLabel, type SeatMode } from './seatMode';

/** Small Odd / Even emblem shown above a card's seat numbers. */
export const SeatModeBadge = ({ mode }: { mode?: SeatMode | null }) =>
  mode ? (
    <span className={`${styles.badge} ${mode === 'odd' ? styles.badgeOdd : styles.badgeEven}`} title={`${seatModeLabel(mode)} seating`}>
      {seatModeLabel(mode)}
    </span>
  ) : null;

type Target = { tpIds: string[]; seats: number[]; currentMode: SeatMode | null; eventName?: string };

type Options = {
  onChanged?: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
};

const list = (seats: number[]) => (seats.length ? seats.join(', ') : 'none');

/**
 * "Change seat numbers": pick Odd or Even for a card, see which seats stay, and confirm. Seats
 * that don't fit are removed from the listing (only before it is on Victory Live).
 */
export function useSeatModeDialog({ onChanged, onSuccess, onError }: Options) {
  const [target, setTarget] = useState<Target | null>(null);
  const [choice, setChoice] = useState<SeatMode | null>(null);
  const [busy, setBusy] = useState(false);

  const open = (next: Target) => {
    setTarget(next);
    setChoice(next.currentMode);
  };
  const close = () => {
    if (!busy) setTarget(null);
  };

  const confirm = async () => {
    if (!target || busy) return;
    setBusy(true);
    try {
      await setSeatMode(target.tpIds, choice);
      onSuccess?.(choice ? `Seats changed to ${choice} numbers only` : 'Odd/even seating cleared');
      setTarget(null);
      onChanged?.();
    } catch (error: any) {
      onError?.(error?.message || 'Failed to change the seat numbers');
    } finally {
      setBusy(false);
    }
  };

  const plan = target ? planSeatMode(target.seats, choice) : null;
  const unchanged = target ? choice === target.currentMode && !plan?.remove.length : true;
  const nothingLeft = Boolean(choice && plan && !plan.keep.length);

  const dialog = target && plan ? (
    <div className={styles.overlay} onClick={close}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Odd/Even seat numbers" onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.title}>Odd/Even seat numbers</h3>
        {target.eventName && <p className={styles.sub}>{target.eventName}</p>}
        <p className={styles.sub}>Current seats: {list([...target.seats].sort((a, b) => a - b))}</p>

        <div className={styles.choices} role="radiogroup" aria-label="Seat numbering">
          {(['odd', 'even'] as SeatMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={choice === mode}
              className={`${styles.choice} ${choice === mode ? styles.choiceOn : ''}`}
              onClick={() => setChoice(mode)}
              disabled={busy}
            >
              {seatModeLabel(mode)}
            </button>
          ))}
          {target.currentMode && (
            <button
              type="button"
              role="radio"
              aria-checked={choice === null}
              className={`${styles.choice} ${choice === null ? styles.choiceOn : ''}`}
              onClick={() => setChoice(null)}
              disabled={busy}
            >
              Remove odd/even
            </button>
          )}
        </div>

        <div className={styles.preview}>
          {choice ? (
            <>
              <div>
                Seats after the change: <strong>{list(plan.keep)}</strong>
              </div>
              {plan.remove.length > 0 && (
                <div className={styles.warn}>
                  Seats {list(plan.remove)} will be removed from this listing ({plan.remove.length}{' '}
                  {plan.remove.length === 1 ? 'ticket' : 'tickets'}).
                </div>
              )}
              {nothingLeft && <div className={styles.warn}>None of these seats are {choice}, so nothing would be left.</div>}
              <div className={styles.note}>"Odd / Even seating" is added to the internal and external notes.</div>
            </>
          ) : (
            <div>Seats stay as they are. The Odd/Even badge and the note are removed.</div>
          )}
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={close} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={styles.confirm} onClick={() => void confirm()} disabled={busy || unchanged || nothingLeft}>
            {busy ? 'Saving…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { open, dialog };
}
